import CheckIcon from '@mui/icons-material/Check'
import { Box, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import eyedrop from '@/assets/images/enamelEyeDrop.svg'
import Button from '@/components/Button'
import Card from '@/components/Card'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import Select from '@/components/Select'
import SnackbarTooltip from '@/components/SnackbarTooltip'
import { useAppSelector } from '@/hooks/redux'
import { Metadata } from '@/pages/stainingStudio/crown/StaingingStudio'
import { useGetMetadataQuery } from '@/store/apiService'
import {
  setEnamelCeramicBrand,
  setEnamelTypeByRegion,
  setMeasuringRegion,
} from '@/store/pfmCrown'
import { REGION } from '@/utils/constants'
import { getOptions } from '@/utils/helper'

function EnamelPanel({
  containerRef,
}: {
  containerRef?: React.RefObject<HTMLDivElement>
}) {
  const { t } = useTranslation('translation', { keyPrefix: 'enamel' })
  const { t: translate } = useTranslation()
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const dispatch = useDispatch()
  const { data: metadata } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as {
    data: Metadata
    error: any
    isLoading: boolean
  }
  const options = useMemo(
    () => ({
      ceramicBrands: getOptions(metadata, 'available_ceramic_brands'),
      skinEnamelTypes: getOptions(metadata, 'available_skinenameltypes'),
    }),
    [metadata],
  )

  const enamelCeramicBrand = useAppSelector(
    (state) => state.pfmCrown.enamelCeramicBrand,
  )

  const enamelTypesByRegion = useAppSelector(
    (state) => state.pfmCrown.enamelTypesByRegion,
  )

  const measuringRegion = useAppSelector(
    (state) => state.pfmCrown.measuringRegion,
  )

  const { control, setValue } = useForm({
    defaultValues: {
      ceramicBrands: enamelCeramicBrand,
      enamelType: enamelTypesByRegion,
    },
    mode: 'onChange',
  })

  React.useEffect(() => {
    Object.entries(enamelTypesByRegion).forEach(([region, value]) => {
      setValue(`enamelType.${region}`, value)
    })

    const allRegions = Object.values(REGION).map((r) =>
      r.split('.')[1].toLowerCase(),
    )
    allRegions.forEach((region) => {
      if (!enamelTypesByRegion[region]) {
        setValue(`enamelType.${region}`, undefined)
      }
    })
  }, [enamelTypesByRegion, setValue])

  useEffect(() => {
    if (!measuringRegion) {
      setSnackbarOpen(false)
    }
  }, [measuringRegion])

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      gap={1}
      overflow="auto"
    >
      <Card>
        <Select
          options={options.ceramicBrands}
          title={t('ceramicBrand')}
          tooltipText={t('selectCeramicBrand')}
          control={control}
          name="ceramicBrands"
          onFormValueChange={(name, value) => {
            dispatch(setEnamelCeramicBrand(value))
          }}
          titleSx={{ fontSize: '18px', fontWeight: 400 }}
          placeholder="Select a Brand"
        />
      </Card>
      {Object.values(REGION).map((region) => (
        <Card key={region}>
          <Box display="flex" flexDirection="column" gap={2}>
            <LabelWithTooltip
              label={`${t('skinEnamelType')}: ${translate(region)}`}
              tooltipText={t('skinEnamelTypeTooltip')}
            />

            <Box gap={1}>
              <Typography sx={{ fontSize: '16px', fontWeight: 400 }}>
                {t('enamelType')}
              </Typography>
              <Select
                options={options.skinEnamelTypes}
                control={control}
                name={`enamelType.${region.split('.')[1].toLowerCase()}`}
                onFormValueChange={(name, value) => {
                  dispatch(
                    setEnamelTypeByRegion({
                      region: region.split('.')[1].toLowerCase(),
                      value,
                    }),
                  )
                }}
              />
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              flexDirection={'column'}
              gap={1}
            >
              <Typography fontSize={'16px'} fontWeight={400}>
                {translate(region)}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                label={t('measure')}
                endIcon={
                  measuringRegion === region.split('.')[1].toLowerCase() ? (
                    <CheckIcon fontSize="small" />
                  ) : (
                    <img src={eyedrop} alt="Select" width={16} height={16} />
                  )
                }
                sx={{ alignSelf: 'flex-start' }}
                textTransform="uppercase"
                onClick={() => {
                  const selectedEnamel =
                    enamelTypesByRegion?.[region.split('.')[1].toLowerCase()]

                  if (!enamelCeramicBrand) {
                    toast.error(t('selectCeramicBrand'))
                    return
                  }

                  if (!selectedEnamel) {
                    toast.error(
                      t('selectEnamel', { region: translate(region) }),
                    )
                    return
                  }
                  dispatch(setMeasuringRegion(region.split('.')[1]))
                  setSnackbarOpen(true)
                }}
              />
            </Box>
          </Box>
        </Card>
      ))}
      <SnackbarTooltip
        containerRef={containerRef}
        message="Click on the spot you want to measure."
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        placement="top"
        offset={[0, -100]}
        maxWidth={500}
        autoHideDuration={6000}
      />
    </Box>
  )
}

export default EnamelPanel
